import { useQuery } from "@tanstack/react-query";
import { checkinApi } from "@bw/shared/api";
import type { CheckinPassResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Client refetch cadence (~60s). The server token TTL is 5 minutes, so a fresh
 * token is always fetched well before the displayed one expires.
 */
export const PASS_REFRESH_MS = 60_000;

/**
 * The short-lived QR check-in pass for the caller's CURRENT drop-in session at a
 * facility (GET /checkin/dropin/{facilityId}/pass). The backend mints it for the
 * player's active OR `pending_validation` session; staff scan it to validate a
 * pending player onto the floor (the staff QR-validation gate).
 *
 * A faithful clone of `useBookingPass`: `enabled` gates the fetch on the caller
 * actually needing the pass (the card only mounts in `pending_validation`), so
 * no background pass is minted otherwise. While enabled it auto-refetches every
 * ~60s and the returned `refetch` drives the manual refresh — the displayed
 * token never goes stale. The httpClient rejects on 404/403/409 (never a
 * fabricated pass), so the card can surface an honest error with NO QR rendered.
 * `gcTime`/`staleTime` 0 so a stale token is never served from cache.
 */
export function useCheckinPass(
  facilityId: string | undefined,
  enabled: boolean,
) {
  return useQuery<CheckinPassResponse>({
    queryKey: queryKeys.dropin.pass(facilityId ?? ""),
    queryFn: () => checkinApi.getDropinPass(facilityId as string),
    enabled: enabled && Boolean(facilityId),
    refetchInterval: enabled ? PASS_REFRESH_MS : false,
    gcTime: 0,
    staleTime: 0,
  });
}
